/* ============================================================
   js/voting.js  —  ChaosStudio Modular Voting Engine
   ============================================================
   ARCHITECTURE LAYER: Application Layer

   PURPOSE:
   - Decoupled voting logic — separate from rendering
   - Thumbs-up / thumbs-down voting, max thumbs-up wins the battle
   - Session-based throttle + anti-spam controls
   - Optimistic UI updates via cache layer
   - One vote per user per design per session
   - Works with or without auth (guest fingerprint fallback)

   USAGE:
     import { VotingEngine } from './voting.js';
     await VotingEngine.vote(entryId, 'up');   // or 'down'
     const state = VotingEngine.getVoteState(entryId);

   ANTI-SPAM LAYERS:
   1. Throttle: min 2s between any two votes
   2. Session cap: max 50 votes per session per week
   3. Per-entry dedup: can't vote same entry twice
   4. Firestore dedup: server-side voteKey check (in firebase.js)
   ============================================================ */

import { VOTE_CONFIG, isBattlesAllowed } from './config.js';
import { DataCache } from './cache.js';

/* ──────────────────────────────────────────────────────────
   VOTE TYPES
   ────────────────────────────────────────────────────────── */
export const VOTE_TYPE = {
  UP:   'up',
  DOWN: 'down',
};

/* ──────────────────────────────────────────────────────────
   VOTING ENGINE
   ────────────────────────────────────────────────────────── */
class _VotingEngine {
  constructor() {
    this._lastVoteTime  = 0;
    this._listeners     = {};
    this._sessionVotes  = this._loadSessionVotes();
    this._pendingVotes  = new Set(); // entry IDs currently being submitted
  }

  /* ────────────────────────────────────────────────────────
     PUBLIC API
     ──────────────────────────────────────────────────────── */

  /**
   * vote(entryId, type, options)
   * Cast a vote on a design entry.
   *
   * entryId : Firestore document ID of the battle_entries doc
   * type    : 'up' | 'down'
   * options.castVoteFn   : async (entryId, type) => void — your Firestore call
   * options.weekId       : current week string, e.g. '2026-W11'
   * options.againstId    : optional ID of the opponent (for matchup dedup)
   *
   * Returns: { success, optimisticCount, error }
   */
  async vote(entryId, type = VOTE_TYPE.UP, options = {}) {
    // 1. Feature flag check
    if (!isBattlesAllowed()) {
      return { success: false, error: 'Battles are currently disabled.' };
    }

    // 2. Auth gate (if enabled)
    if (VOTE_CONFIG.REQUIRE_AUTH && !this._isLoggedIn()) {
      this._emit('auth_required', { entryId, type });
      return { success: false, error: 'login_required' };
    }

    // 3. Throttle check — min interval between votes
    const now    = Date.now();
    const since  = now - this._lastVoteTime;
    if (since < VOTE_CONFIG.VOTE_THROTTLE_MS) {
      const wait = Math.ceil((VOTE_CONFIG.VOTE_THROTTLE_MS - since) / 1000);
      return { success: false, error: `throttled`, waitMs: VOTE_CONFIG.VOTE_THROTTLE_MS - since };
    }

    // 4. Duplicate vote check — already voted this entry this session
    if (this._hasVotedEntry(entryId)) {
      return { success: false, error: 'already_voted' };
    }

    // 5. Session cap check
    const weekId       = options.weekId || this._getCurrentWeekId();
    const sessionCount = this._getSessionVoteCount(weekId);
    if (sessionCount >= VOTE_CONFIG.MAX_VOTES_PER_SESSION) {
      return { success: false, error: 'session_cap_reached' };
    }

    // 6. Prevent duplicate concurrent submissions
    if (this._pendingVotes.has(entryId)) {
      return { success: false, error: 'vote_pending' };
    }

    // ── Optimistic update ──
    let optimisticCount = null;
    if (VOTE_CONFIG.OPTIMISTIC_UPDATES && type === VOTE_TYPE.UP) {
      optimisticCount = DataCache.optimisticVoteIncrement(entryId);
      this._emit('optimistic_vote', { entryId, type, count: optimisticCount });
    }

    // Mark throttle + session immediately (before async call)
    this._lastVoteTime = now;
    this._pendingVotes.add(entryId);

    try {
      // Call the provided Firestore function
      if (options.castVoteFn) {
        await options.castVoteFn(entryId, type, options.againstId, weekId);
      }

      // Record in session state
      this._recordSessionVote(entryId, type, weekId);
      this._emit('vote_success', { entryId, type, optimisticCount });

      return { success: true, optimisticCount };

    } catch (err) {
      // Rollback optimistic count on failure
      if (optimisticCount !== null) {
        DataCache.setVoteCount(entryId, optimisticCount - 1);
        this._emit('vote_rollback', { entryId, type });
      }

      const isAlreadyVoted = err.message === 'already_voted';
      if (isAlreadyVoted) {
        // Sync session state with server reality
        this._recordSessionVote(entryId, type, weekId);
      }

      this._emit('vote_error', { entryId, type, error: err.message });
      return { success: false, error: err.message };

    } finally {
      this._pendingVotes.delete(entryId);
    }
  }

  /**
   * getVoteState(entryId)
   * Returns the current known vote state for a design.
   * { voted, voteType, count }
   */
  getVoteState(entryId) {
    const sessionVote = this._sessionVotes.entries?.[entryId];
    const count       = DataCache.getVoteCount(entryId);
    return {
      voted:    !!sessionVote,
      voteType: sessionVote?.type || null,
      count:    count ?? null,
    };
  }

  /**
   * canVote(entryId)
   * Returns { allowed, reason } — use to gate vote button UI.
   */
  canVote(entryId) {
    if (!isBattlesAllowed()) return { allowed: false, reason: 'battles_disabled' };
    if (VOTE_CONFIG.REQUIRE_AUTH && !this._isLoggedIn()) return { allowed: false, reason: 'login_required' };
    if (this._hasVotedEntry(entryId))   return { allowed: false, reason: 'already_voted' };
    if (this._pendingVotes.has(entryId)) return { allowed: false, reason: 'vote_pending' };
    const throttleMsLeft = VOTE_CONFIG.VOTE_THROTTLE_MS - (Date.now() - this._lastVoteTime);
    if (throttleMsLeft > 0) return { allowed: false, reason: 'throttled', waitMs: throttleMsLeft };
    return { allowed: true, reason: null };
  }

  /**
   * getLeaderboard(entries)
   * Sorts entries by vote count descending — pure utility, no I/O.
   * entries: array of { id, votes, userName, imageUrl, ... }
   * Returns sorted array with rank added.
   */
  getLeaderboard(entries) {
    return [...entries]
      .sort((a, b) => (b.votes || 0) - (a.votes || 0))
      .map((entry, i) => ({ ...entry, rank: i + 1 }));
  }

  /**
   * getSessionStats()
   * Returns how many votes this session has cast this week.
   */
  getSessionStats() {
    const weekId = this._getCurrentWeekId();
    return {
      weekId,
      count:     this._getSessionVoteCount(weekId),
      remaining: Math.max(0, VOTE_CONFIG.MAX_VOTES_PER_SESSION - this._getSessionVoteCount(weekId)),
      cap:       VOTE_CONFIG.MAX_VOTES_PER_SESSION,
    };
  }

  /**
   * resetSession()
   * Clears session vote history — useful for dev/testing only.
   */
  resetSession() {
    this._sessionVotes = { entries: {}, weeks: {} };
    this._saveSessionVotes();
    console.log('[Voting] Session reset.');
  }

  /* ────────────────────────────────────────────────────────
     EVENT EMITTER
     ──────────────────────────────────────────────────────── */

  /**
   * on(event, callback) — subscribe to voting events
   * Events:
   *   'optimistic_vote'  — immediate UI update before server confirm
   *   'vote_success'     — server confirmed
   *   'vote_rollback'    — server rejected, undo optimistic update
   *   'vote_error'       — general error
   *   'auth_required'    — vote attempted but not logged in
   */
  on(event, callback) {
    if (!this._listeners[event]) this._listeners[event] = [];
    this._listeners[event].push(callback);
    return () => this.off(event, callback);
  }

  off(event, callback) {
    if (this._listeners[event]) {
      this._listeners[event] = this._listeners[event].filter(fn => fn !== callback);
    }
  }

  _emit(event, data) {
    (this._listeners[event] || []).forEach(fn => {
      try { fn(data); } catch(e) { console.error('[Voting] Listener error:', e); }
    });
  }

  /* ────────────────────────────────────────────────────────
     SESSION STATE  (localStorage)
     ──────────────────────────────────────────────────────── */

  _loadSessionVotes() {
    try {
      const raw = localStorage.getItem(VOTE_CONFIG.SESSION_KEY);
      return raw ? JSON.parse(raw) : { entries: {}, weeks: {} };
    } catch { return { entries: {}, weeks: {} }; }
  }

  _saveSessionVotes() {
    try {
      localStorage.setItem(VOTE_CONFIG.SESSION_KEY, JSON.stringify(this._sessionVotes));
    } catch {}
  }

  _hasVotedEntry(entryId) {
    return !!this._sessionVotes.entries?.[entryId];
  }

  _recordSessionVote(entryId, type, weekId) {
    if (!this._sessionVotes.entries) this._sessionVotes.entries = {};
    if (!this._sessionVotes.weeks)   this._sessionVotes.weeks   = {};
    this._sessionVotes.entries[entryId]    = { type, at: Date.now() };
    this._sessionVotes.weeks[weekId]       = (this._sessionVotes.weeks[weekId] || 0) + 1;
    this._saveSessionVotes();
  }

  _getSessionVoteCount(weekId) {
    return this._sessionVotes.weeks?.[weekId] || 0;
  }

  _getCurrentWeekId() {
    const now  = new Date();
    const jan1 = new Date(now.getFullYear(), 0, 1);
    const week = Math.ceil((((now - jan1) / 86400000) + jan1.getDay() + 1) / 7);
    return `${now.getFullYear()}-W${String(week).padStart(2, '0')}`;
  }

  _isLoggedIn() {
    // Light check — firebase auth state is managed by firebase.js
    // This avoids importing firebase here to keep module independent
    return !!window._csCurrentUser;
  }
}

/* ──────────────────────────────────────────────────────────
   VOTE BUTTON RENDERER
   Drop-in vote button component — handles all UI states
   ────────────────────────────────────────────────────────── */
export class VoteButton {
  /**
   * render(entryId, currentCount, type)
   * Returns an HTML string for a vote button.
   * type: 'up' | 'down'
   */
  static render(entryId, currentCount = 0, type = VOTE_TYPE.UP) {
    const state   = VotingEngine.getVoteState(entryId);
    const canVote = VotingEngine.canVote(entryId);
    const voted   = state.voted && state.voteType === type;
    const count   = state.count ?? currentCount;
    const icon    = type === VOTE_TYPE.UP ? '👍' : '👎';
    const label   = type === VOTE_TYPE.UP ? 'Up' : 'Down';

    return `
      <button
        class="vote-btn vote-btn--${type} ${voted ? 'vote-btn--voted' : ''} ${!canVote.allowed ? 'vote-btn--disabled' : ''}"
        data-entry-id="${entryId}"
        data-vote-type="${type}"
        onclick="window._csVote && window._csVote('${entryId}', '${type}', this)"
        title="${voted ? `You voted ${label}` : `Vote ${label}`}"
        ${!canVote.allowed && canVote.reason !== 'throttled' ? 'disabled' : ''}>
        <span class="vote-btn-icon">${icon}</span>
        <span class="vote-btn-count" data-vote-count="${entryId}-${type}">${count}</span>
      </button>`;
  }

  /**
   * injectStyles()
   * Call once per page.
   */
  static injectStyles() {
    if (document.getElementById('cs-vote-styles')) return;
    const s = document.createElement('style');
    s.id = 'cs-vote-styles';
    s.textContent = `
      .vote-btn {
        display: inline-flex; align-items: center; gap: 6px;
        padding: 7px 14px; border-radius: 100px;
        border: 1.5px solid rgba(255,255,255,.12);
        background: rgba(255,255,255,.04);
        color: #aaa; font-size: .82rem; font-weight: 700;
        cursor: pointer; transition: all .18s;
        font-family: inherit; user-select: none;
      }
      .vote-btn:hover:not(:disabled) {
        border-color: rgba(255,229,0,.4);
        color: #ffe500;
        background: rgba(255,229,0,.06);
        transform: scale(1.05);
      }
      .vote-btn--up.vote-btn--voted {
        border-color: rgba(57,255,20,.5);
        background: rgba(57,255,20,.1);
        color: #39ff14;
      }
      .vote-btn--down.vote-btn--voted {
        border-color: rgba(255,45,120,.4);
        background: rgba(255,45,120,.08);
        color: #ff2d78;
      }
      .vote-btn--disabled { opacity: .45; cursor: not-allowed; }
      .vote-btn--loading .vote-btn-icon { animation: cs-vote-spin .6s linear infinite; }
      .vote-btn-count { min-width: 16px; text-align: center; }
      @keyframes cs-vote-spin { to { transform: rotate(360deg); } }

      .vote-pair { display: flex; gap: 8px; align-items: center; }
      .vote-winner-badge {
        display: inline-flex; align-items: center; gap: 4px;
        font-size: .65rem; font-weight: 800; letter-spacing: .1em;
        text-transform: uppercase; color: #ffe500;
        background: rgba(255,229,0,.12); border: 1px solid rgba(255,229,0,.25);
        border-radius: 100px; padding: 2px 8px;
      }
    `;
    document.head.appendChild(s);
  }

  /**
   * updateCount(entryId, type, newCount)
   * Updates the displayed vote count in-place without re-rendering.
   */
  static updateCount(entryId, type, newCount) {
    document.querySelectorAll(`[data-vote-count="${entryId}-${type}"]`)
      .forEach(el => { el.textContent = newCount; });
  }

  /**
   * setVoted(entryId, type)
   * Visually marks a button as voted.
   */
  static setVoted(entryId, type) {
    document.querySelectorAll(`[data-entry-id="${entryId}"][data-vote-type="${type}"]`)
      .forEach(btn => {
        btn.classList.add('vote-btn--voted');
        btn.classList.remove('vote-btn--loading');
        btn.disabled = true;
      });
  }

  /**
   * setLoading(entryId, type, loading)
   */
  static setLoading(entryId, type, loading) {
    document.querySelectorAll(`[data-entry-id="${entryId}"][data-vote-type="${type}"]`)
      .forEach(btn => btn.classList.toggle('vote-btn--loading', loading));
  }
}

/* ──────────────────────────────────────────────────────────
   GLOBAL VOTE HANDLER
   Bound to window so inline onclick="window._csVote(...)" works
   ────────────────────────────────────────────────────────── */
export function installGlobalVoteHandler(castVoteFn, weekId) {
  window._csVote = async function(entryId, type, btnEl) {
    VoteButton.setLoading(entryId, type, true);

    const result = await VotingEngine.vote(entryId, type, {
      castVoteFn,
      weekId,
    });

    VoteButton.setLoading(entryId, type, false);

    if (result.success) {
      VoteButton.setVoted(entryId, type);
      if (result.optimisticCount !== null) {
        VoteButton.updateCount(entryId, type, result.optimisticCount);
      }
    } else {
      // Show inline feedback
      const messages = {
        already_voted:      'Already voted!',
        throttled:          'Slow down…',
        login_required:     'Sign in to vote',
        session_cap_reached:'You\'ve voted a lot today!',
        battles_disabled:   'Battles are paused',
      };
      const msg = messages[result.error] || 'Could not vote';
      _showVoteToast(msg);
    }
  };
}

function _showVoteToast(msg) {
  let t = document.getElementById('vote-toast');
  if (!t) {
    t = document.createElement('div');
    t.id = 'vote-toast';
    t.style.cssText = `
      position:fixed; bottom:24px; left:50%; transform:translateX(-50%) translateY(20px);
      background:#1a1a1a; border:1px solid #333; color:#ccc;
      padding:8px 18px; border-radius:100px; font-size:.82rem; font-weight:600;
      z-index:9999; opacity:0; transition:all .22s; pointer-events:none;
      white-space:nowrap;
    `;
    document.body.appendChild(t);
  }
  t.textContent = msg;
  t.style.opacity = '1';
  t.style.transform = 'translateX(-50%) translateY(0)';
  clearTimeout(t._t);
  t._t = setTimeout(() => {
    t.style.opacity = '0';
    t.style.transform = 'translateX(-50%) translateY(20px)';
  }, 2200);
}

/* ──────────────────────────────────────────────────────────
   LEADERBOARD RENDERER
   ────────────────────────────────────────────────────────── */
export class LeaderboardRenderer {
  /**
   * render(entries, containerEl)
   * Renders a ranked leaderboard into containerEl.
   * entries: array of { id, userName, imageUrl, votes, vibe }
   */
  static render(entries, containerEl) {
    if (!containerEl) return;
    const ranked = VotingEngine.getLeaderboard(entries);
    const top3   = ranked.slice(0, 3);
    const rest   = ranked.slice(3);

    containerEl.innerHTML = `
      <div class="leaderboard">
        <div class="leaderboard-podium">
          ${top3.map(e => LeaderboardRenderer._podiumCard(e)).join('')}
        </div>
        ${rest.length > 0 ? `
          <div class="leaderboard-list">
            ${rest.map(e => LeaderboardRenderer._listRow(e)).join('')}
          </div>
        ` : ''}
      </div>`;

    // Inject styles once
    if (!document.getElementById('cs-leaderboard-styles')) {
      const s = document.createElement('style');
      s.id = 'cs-leaderboard-styles';
      s.textContent = `
        .leaderboard-podium {
          display: flex; gap: 16px; justify-content: center;
          flex-wrap: wrap; margin-bottom: 24px;
        }
        .podium-card {
          background: #161616; border: 1px solid #2a2a2a;
          border-radius: 14px; padding: 16px;
          text-align: center; width: 180px;
          transition: transform .18s;
        }
        .podium-card:hover { transform: translateY(-3px); }
        .podium-card.rank-1 { border-color: rgba(255,229,0,.4); }
        .podium-card.rank-2 { border-color: rgba(192,192,192,.3); }
        .podium-card.rank-3 { border-color: rgba(205,127,50,.3); }
        .podium-rank { font-size: 1.4rem; margin-bottom: 6px; }
        .podium-img {
          width: 100%; height: 120px; object-fit: cover;
          border-radius: 8px; background: #1a1a1a;
          margin-bottom: 8px;
        }
        .podium-name { font-size: .75rem; color: #ccc; margin-bottom: 4px; }
        .podium-votes { font-size: 1.1rem; font-weight: 800; color: #ffe500; }
        .podium-votes span { font-size: .65rem; color: #666; font-weight: 400; }
        .leaderboard-list { display: flex; flex-direction: column; gap: 8px; }
        .lb-row {
          display: flex; align-items: center; gap: 12px;
          background: #111; border: 1px solid #222; border-radius: 10px;
          padding: 10px 14px;
        }
        .lb-rank { font-size: .85rem; color: #555; font-weight: 700; min-width: 24px; }
        .lb-img { width: 40px; height: 40px; border-radius: 6px; object-fit: cover; background: #1a1a1a; }
        .lb-name { flex: 1; font-size: .82rem; color: #ccc; }
        .lb-votes { font-size: .9rem; font-weight: 800; color: #ffe500; }
      `;
      document.head.appendChild(s);
    }
  }

  static _podiumCard(entry) {
    const medals = ['🥇', '🥈', '🥉'];
    return `
      <div class="podium-card rank-${entry.rank}">
        <div class="podium-rank">${medals[entry.rank - 1] || `#${entry.rank}`}</div>
        ${entry.imageUrl
          ? `<img class="podium-img" src="${entry.imageUrl}" alt="${entry.userName}" loading="lazy">`
          : `<div class="podium-img" style="display:flex;align-items:center;justify-content:center;font-size:2rem">👕</div>`}
        <div class="podium-name">@${entry.userName || 'Anonymous'}</div>
        <div class="podium-votes">${entry.votes || 0} <span>votes</span></div>
      </div>`;
  }

  static _listRow(entry) {
    return `
      <div class="lb-row">
        <div class="lb-rank">#${entry.rank}</div>
        ${entry.imageUrl
          ? `<img class="lb-img" src="${entry.imageUrl}" alt="${entry.userName}" loading="lazy">`
          : `<div class="lb-img" style="display:flex;align-items:center;justify-content:center">👕</div>`}
        <div class="lb-name">@${entry.userName || 'Anonymous'}</div>
        <div class="lb-votes">${entry.votes || 0} 👍</div>
      </div>`;
  }
}

/* ──────────────────────────────────────────────────────────
   SINGLETON EXPORT
   ────────────────────────────────────────────────────────── */
export const VotingEngine = new _VotingEngine();

export default VotingEngine;
